import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { 
  FileText, Users, Wallet, BarChart3, CheckCircle, ArrowRight,
  Smartphone, Monitor, Clock, FolderOpen, TrendingUp, Bell,
  CreditCard, PieChart, ArrowUpRight, ArrowDownRight, Menu, X
} from "lucide-react";
import { Button } from "../../components/ui/button";
import CursorGlow from "../../components/CursorGlow";

const trustItems = [
  { icon: Clock, text: "Quick setup" },
  { icon: Users, text: "Client management" },
  { icon: Wallet, text: "AI-powered insights" },
  { icon: Smartphone, text: "Works everywhere" }
];

const features = [
  {
    icon: FileText,
    title: "Smart Invoicing",
    description: "AI-assisted invoice creation with automated payment tracking and reminders."
  },
  {
    icon: BarChart3,
    title: "Financial Analytics",
    description: "Real-time dashboards with intelligent insights and recommendations."
  },
  {
    icon: Wallet,
    title: "Automated Cash Flow",
    description: "Intelligent cash tracking that updates automatically as payments come in."
  },
  {
    icon: Users,
    title: "Client Intelligence",
    description: "Smart client profiles with payment history and revenue analytics."
  }
];

const steps = [
  { number: "1", text: "Create your business profile (name + logo)" },
  { number: "2", text: "Issue an invoice (select a client, set a due date)" },
  { number: "3", text: "Track pending and overdue (dashboard updates instantly)" },
  { number: "4", text: "Mark paid → cash-in is recorded (reports update)" },
  { number: "5", text: "Track Financial Reports" }
];

const mockInvoices = [
  { client: "Acme Corp", amount: "$2,450.00", status: "pending", due: "Jan 15" },
  { client: "TechStart Inc", amount: "$1,890.00", status: "overdue", due: "Dec 28" },
  { client: "Design Studio", amount: "$3,200.00", status: "paid", due: "Jan 5" },
];

export default function LandingHome() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);
  const [searchParams] = useSearchParams();
  const [referralPartner, setReferralPartner] = useState(null);

  // Capture referral code from URL and store in localStorage
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      localStorage.setItem('ledgeramt_referral_code', refCode);
      
      // Lookup partner info
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      fetch(`${apiUrl}/api/referral/lookup?code=${refCode}`)
        .then(res => res.json())
        .then(data => {
          if (data.valid) {
            setReferralPartner(data);
          }
        })
        .catch((err) => console.log('Referral lookup failed:', err));
    }
  }, [searchParams]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setMousePosition({
          x: (e.clientX - rect.left) / rect.width,
          y: (e.clientY - rect.top) / rect.height,
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="min-h-screen bg-emerald-950 text-white overflow-hidden">
      <CursorGlow />
      
      {/* Referral Banner */}
      {referralPartner && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-emerald-500/20 border-b border-emerald-500/30 py-2.5 px-4 text-center backdrop-blur-sm">
          <p className="text-emerald-300 text-sm">
            <span className="font-semibold">Referred by {referralPartner.partner_name}</span>
            <span className="mx-2">•</span>
            <Link to="/login" className="text-emerald-400 hover:text-emerald-300 underline">Sign in now</Link>
          </p>
        </div>
      )}
      
      {/* Navigation */}
      <nav className={`fixed left-0 right-0 z-50 bg-emerald-950/80 backdrop-blur-xl border-b border-emerald-800/30 ${referralPartner ? 'top-10' : 'top-0'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center group">
              <img 
                src="/ledgeramt-logo.png" 
                alt="LedgerAMT" 
                className="h-20 sm:h-24 w-auto transition-transform duration-300 group-hover:scale-105"
              />
            </Link>
            
            <div className="hidden md:flex items-center gap-10">
              {['Home', 'Features', 'Pricing', 'About', 'Contact'].map((item, i) => (
                <Link 
                  key={item}
                  to={item === 'Home' ? '/' : `/${item.toLowerCase()}`}
                  className={`text-sm font-medium transition-all duration-300 hover:text-emerald-400 relative group ${
                    i === 0 ? 'text-emerald-300' : 'text-emerald-400/70'
                  }`}
                >
                  {item}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-emerald-400 transition-all duration-300 group-hover:w-full"></span>
                </Link>
              ))}
            </div>

            <div className="hidden md:flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" size="sm" className="text-emerald-300 hover:text-white hover:bg-emerald-800/50 transition-all duration-300">
                  Log in
                </Button>
              </Link>
              <Link to="/login">
                <Button size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold px-6 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/30 hover:-translate-y-0.5">
                  Sign in
                </Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden text-emerald-300 p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-emerald-950/95 backdrop-blur-xl border-t border-emerald-800/30 animate-in slide-in-from-top duration-300">
            <div className="px-4 py-6 space-y-4">
              {['Home', 'Features', 'Pricing', 'About', 'Contact'].map((item) => (
                <Link 
                  key={item}
                  to={item === 'Home' ? '/' : `/${item.toLowerCase()}`}
                  className="block text-emerald-300 hover:text-white py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item}
                </Link>
              ))}
              <div className="pt-4 flex gap-3">
                <Link to="/login" className="flex-1">
                  <Button variant="outline" className="w-full border-emerald-500/50 text-emerald-300">Log in</Button>
                </Link>
                <Link to="/login" className="flex-1">
                  <Button className="w-full bg-emerald-500 text-emerald-950">Sign in</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section ref={heroRef} className="relative pt-32 sm:pt-40 pb-20 px-4 sm:px-6 lg:px-8">
        {/* Animated background orbs */}
        <div 
          className="absolute top-20 left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl transition-transform duration-1000 ease-out"
          style={{
            transform: `translate(${mousePosition.x * 30}px, ${mousePosition.y * 30}px)`,
          }}
        ></div>
        <div 
          className="absolute top-40 right-1/4 w-80 h-80 bg-emerald-400/10 rounded-full blur-3xl transition-transform duration-1000 ease-out"
          style={{
            transform: `translate(${-mousePosition.x * 20}px, ${-mousePosition.y * 20}px)`,
          }}
        ></div>
        <div 
          className="absolute bottom-20 left-1/3 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl transition-transform duration-1000 ease-out"
          style={{
            transform: `translate(${mousePosition.x * 15}px, ${-mousePosition.y * 15}px)`,
          }}
        ></div>
        
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Text Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-300 px-4 py-2 rounded-full text-sm font-medium mb-8 border border-emerald-500/30 animate-pulse">
                <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
                AI-Powered Accounting
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight mb-8">
                <span className="inline-block animate-in fade-in slide-in-from-bottom-4 duration-700">Smart accounting.</span>
                <br />
                <span className="inline-block text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-emerald-300 to-teal-400 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-150">
                  Powered by AI.
                </span>
              </h1>
              
              <p className="text-xl text-emerald-200/80 mb-10 max-w-xl leading-relaxed animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300">
                AI-powered invoicing, automated cash flow tracking, and intelligent financial insights. 
                The smart way to manage your business finances.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-in fade-in slide-in-from-bottom-4 duration-700 delay-500">
                <Link to="/login">
                  <Button size="lg" className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold text-lg px-8 h-14 rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/30 hover:-translate-y-1 active:translate-y-0">
                    Sign In
                    <ArrowRight className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                  </Button>
                </Link>
                <Link to="/features">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 hover:border-emerald-400 text-lg px-8 h-14 rounded-xl transition-all duration-300">
                    See how it works
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right - Mock Dashboard */}
            <div className="relative animate-in fade-in slide-in-from-right duration-1000 delay-300">
              {/* Main Dashboard */}
              <div 
                className="bg-gradient-to-br from-emerald-900/80 to-emerald-950 rounded-3xl p-6 border border-emerald-700/30 shadow-2xl shadow-emerald-500/10 transition-transform duration-500 hover:scale-[1.02]"
                style={{
                  transform: `perspective(1000px) rotateY(${(mousePosition.x - 0.5) * 5}deg) rotateX(${-(mousePosition.y - 0.5) * 5}deg)`,
                }}
              >
                {/* Dashboard Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-emerald-400/60 text-sm">Good morning</p>
                    <h3 className="text-xl font-semibold text-white">Your Dashboard</h3>
                  </div>
                  <div className="flex gap-2">
                    <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center animate-pulse">
                      <Bell className="w-4 h-4 text-emerald-400" />
                    </div>
                  </div>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-emerald-800/30 rounded-2xl p-4 transition-all duration-300 hover:bg-emerald-800/50 hover:scale-105 cursor-pointer">
                    <p className="text-emerald-400/60 text-xs mb-1">Total Balance</p>
                    <p className="text-2xl font-bold text-white">$24,580</p>
                    <div className="flex items-center gap-1 text-emerald-400 text-xs mt-1">
                      <ArrowUpRight className="w-3 h-3" />
                      <span>+12%</span>
                    </div>
                  </div>
                  <div className="bg-emerald-800/30 rounded-2xl p-4 transition-all duration-300 hover:bg-emerald-800/50 hover:scale-105 cursor-pointer">
                    <p className="text-emerald-400/60 text-xs mb-1">Cash In</p>
                    <p className="text-2xl font-bold text-emerald-400">$8,450</p>
                    <div className="flex items-center gap-1 text-emerald-400 text-xs mt-1">
                      <ArrowUpRight className="w-3 h-3" />
                      <span>+8%</span>
                    </div>
                  </div>
                  <div className="bg-emerald-800/30 rounded-2xl p-4 transition-all duration-300 hover:bg-emerald-800/50 hover:scale-105 cursor-pointer">
                    <p className="text-emerald-400/60 text-xs mb-1">Cash Out</p>
                    <p className="text-2xl font-bold text-rose-400">$3,200</p>
                    <div className="flex items-center gap-1 text-rose-400 text-xs mt-1">
                      <ArrowDownRight className="w-3 h-3" />
                      <span>-3%</span>
                    </div>
                  </div>
                </div>

                {/* Invoices List */}
                <div className="space-y-3">
                  <p className="text-emerald-400/60 text-sm font-medium mb-3">Invoices to collect</p>
                  {mockInvoices.map((invoice, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between bg-emerald-800/20 rounded-xl px-4 py-3 transition-all duration-300 hover:bg-emerald-800/40 hover:translate-x-2 cursor-pointer"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full transition-all duration-300 ${
                          invoice.status === 'paid' ? 'bg-emerald-400 shadow-lg shadow-emerald-400/50' :
                          invoice.status === 'overdue' ? 'bg-rose-400 shadow-lg shadow-rose-400/50 animate-pulse' : 'bg-amber-400'
                        }`}></div>
                        <div>
                          <p className="text-white text-sm font-medium">{invoice.client}</p>
                          <p className="text-emerald-400/50 text-xs">Due {invoice.due}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-semibold">{invoice.amount}</p>
                        <p className={`text-xs capitalize ${
                          invoice.status === 'paid' ? 'text-emerald-400' :
                          invoice.status === 'overdue' ? 'text-rose-400' : 'text-amber-400'
                        }`}>{invoice.status}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Floating elements with animations */}
              <div className="absolute -top-4 -right-4 bg-emerald-500 rounded-2xl px-4 py-3 shadow-lg shadow-emerald-500/30 animate-bounce-slow">
                <p className="text-emerald-950 text-xs font-medium">Invoice Paid</p>
                <p className="text-emerald-950 font-bold">+$3,200</p>
              </div>

              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl px-4 py-3 shadow-lg animate-float">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <div>
                    <p className="text-gray-900 text-sm font-medium">Cash updated</p>
                    <p className="text-gray-500 text-xs">Automatically recorded</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="py-12 border-y border-emerald-800/30 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {trustItems.map((item, index) => (
              <div 
                key={index} 
                className="flex items-center justify-center gap-3 group cursor-pointer"
              >
                <item.icon className="w-5 h-5 text-emerald-400 flex-shrink-0 transition-all duration-300 group-hover:scale-125 group-hover:text-emerald-300" />
                <span className="text-sm font-medium text-emerald-300/80 transition-colors duration-300 group-hover:text-emerald-200">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What LedgerAMT Does */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-900/20 to-transparent"></div>
        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-20">
            <h2 className="text-4xl sm:text-5xl font-bold mb-6">
              What <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">LedgerAMT</span> does
            </h2>
            <p className="text-emerald-300/70 text-lg max-w-2xl mx-auto">
              AI-powered accounting that automates your financial workflows and delivers actionable insights.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="text-center group cursor-pointer"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 rounded-2xl flex items-center justify-center mx-auto mb-6 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 group-hover:shadow-xl group-hover:shadow-emerald-500/20 border border-emerald-500/30">
                  <feature.icon className="w-8 h-8 text-emerald-400 transition-all duration-300 group-hover:text-emerald-300" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3 transition-colors duration-300 group-hover:text-emerald-300">{feature.title}</h3>
                <p className="text-emerald-300/70 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-6">
              How it <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">works</span>
            </h2>
          </div>

          <div className="space-y-6">
            {steps.map((step, index) => (
              <div 
                key={index}
                className="flex items-center gap-6 bg-gradient-to-r from-emerald-900/50 to-transparent rounded-2xl p-6 border-l-4 border-emerald-500 transition-all duration-500 hover:from-emerald-900/70 hover:translate-x-4 hover:border-emerald-400 cursor-pointer group"
              >
                <div className="w-14 h-14 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/30 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3">
                  <span className="text-emerald-950 font-bold text-xl">{step.number}</span>
                </div>
                <p className="text-emerald-100 text-lg transition-colors duration-300 group-hover:text-white">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/50 to-transparent"></div>
        <div className="max-w-4xl mx-auto text-center relative">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            Start with a plan that fits
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">your business</span>
          </h2>
          <p className="text-emerald-300/70 text-lg mb-10">
            You can change plans anytime. No commitment required.
          </p>
          <Link to="/login">
            <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold text-lg px-10 h-14 rounded-xl shadow-lg shadow-emerald-500/30 transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/40 hover:-translate-y-1 active:translate-y-0">
              Sign In
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-emerald-950 border-t border-emerald-800/30 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <img 
                src="/ledgeramt-logo.png" 
                alt="LedgerAMT" 
                className="h-20 w-auto mb-6 transition-transform duration-300 hover:scale-105"
              />
              <p className="text-emerald-400/60 text-sm">
                AI-Powered Accounting for Modern Businesses
              </p>
              <p className="text-emerald-400/80 text-xs mt-2">
                A product of Africa Market for Trade
              </p>
            </div>
            
            {[
              { title: 'Product', links: [{ name: 'Features', to: '/features' }, { name: 'Pricing', to: '/pricing' }] },
              { title: 'Company', links: [{ name: 'About', to: '/about' }, { name: 'Contact', to: '/contact' }] },
              { title: 'Legal', links: [{ name: 'Privacy Policy', to: '/privacy' }, { name: 'Terms of Service', to: '/terms' }] },
              { title: 'Account', links: [{ name: 'Log in', to: '/login' }] },
            ].map((section) => (
              <div key={section.title}>
                <h4 className="font-semibold text-white mb-4">{section.title}</h4>
                <ul className="space-y-3 text-sm text-emerald-400/60">
                  {section.links.map((link) => (
                    <li key={link.name}>
                      <Link to={link.to} className="hover:text-emerald-400 transition-colors duration-300">
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          <div className="border-t border-emerald-800/30 pt-8 text-center text-sm text-emerald-400/50">
            <p>© {new Date().getFullYear()} Africa Market for Trade. All rights reserved.</p>
            <p className="mt-2 text-xs">LedgerAMT™ is a trademark of Africa Market for Trade</p>
          </div>
        </div>
      </footer>

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-5px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
